import _ from "lodash";
import axios from "axios";

import { api } from "./config";
import { ApiConfig, SnackBarConfig } from "./types";
import { methodDisplayNameMapper } from "./consts";
import { Pagination } from "services/search";
import { PaginationDirection } from "services/url";
import { showNotification } from "./snackbar";

/**
 * Check if the error is a network error (API unreachable)
 */
export const isNetworkError = (error: any): boolean => {
  return (
    !error.response && // No response from server
    (error.code === 'ECONNABORTED' || // Connection timeout
      error.code === 'ERR_NETWORK' || // Network error
      error.message === 'Network Error' || // Generic network error
      error.message?.includes('timeout')) // Timeout error
  );
};

/**
 * Check if the error is a server error (5xx)
 */
export const isServerError = (error: any): boolean => {
  return error.response && error.response.status >= 500;
};

/**
 * Get user-friendly error message based on error type
 */
export const getApiErrorMessage = (error: any, serviceName?: string): string => {
  if (isNetworkError(error)) {
    return 'Cannot connect to server. Please check your internet connection and try again.';
  }

  if (isServerError(error)) {
    return 'Server is experiencing issues. Please try again later.';
  }

  if (error.response?.status === 401) {
    return 'Your session has expired. Please log in again.';
  }

  if (error.response?.status === 403) {
    return 'You do not have permission to perform this action.';
  }

  if (error.response?.status === 404) {
    return 'The requested resource was not found.';
  }

  return error.response?.data?.message ||
    (serviceName ? `${serviceName} encountered an error. Please try again` : 'An unexpected error occurred. Please try again.');
};

export const SKIP_OFFLINE_CHECK_HEADER = "X-Skip-Offline-Check";

/**
 * Check API health. Marks the request so the global axios interceptor
 * skips its offline-detection logic (otherwise the interceptor would
 * await the health check, which would await the interceptor = deadlock).
 */
export const checkApiHealth = async (): Promise<boolean> => {
  try {
    const response = await axios.get(`${api.hostname}`, {
      timeout: 10000,
      headers: { [SKIP_OFFLINE_CHECK_HEADER]: "1" },
    });
    return response.status === 200;
  } catch (error) {
    console.error('API Health Check Failed:', error);
    return false;
  }
};

/**
 * Function which creates an API call for given api config and associated action types
 */
export const createApiCall =
  (apiConfig: ApiConfig, snackbarConfig?: SnackBarConfig) => async () => {
    const hostname = apiConfig.hostname || api.hostname;
    const path = apiConfig.path;
    const method = _.get(apiConfig, "method", "GET");
    const contentType =
      _.includes(["POST", "PUT", "PATCH"], method) && !apiConfig.formData
        ? {
          "Content-Type": "application/json",
        }
        : {};
    const passedHeaders = _.get(apiConfig, "headers", {});
    const headers = {
      ...contentType,
      ...passedHeaders,
    };
    const axiosConfig = {
      method,
      headers,
      url: `${hostname}${path}`,
      timeout: apiConfig.timeout ? apiConfig.timeout : 60000,
      data: apiConfig.body,
      responseType: apiConfig.responseType ?? "json",
    };


    // if there is no snackbar return simple axios call
    if (!snackbarConfig) {
      return axios(axiosConfig);
    }

    // snackbarConfig was provided
    const { getMessage, serviceName } = snackbarConfig as any;

    try {
      const response = await axios(axiosConfig);

      // show success snack bar
      const methodDisplayName = methodDisplayNameMapper[method.toLowerCase()];
      const successMessage = getMessage
        ? getMessage.success && getMessage.success(response)
        : `${serviceName} successfully ${methodDisplayName}`;

      if (successMessage) {
        showNotification(successMessage, { variant: "success" });
      }

      return response;
    } catch (error: any) {
      // 401 = istekao session. Auth interceptor već handle-a (refresh token / sign-out + redirect na login).
      // Ne logiramo console.error niti prikazujemo snackbar — korisnik je već na login stranici.
      const isUnauthorized = error?.response?.status === 401;

      // show error snack bar (osim za 401)
      if (snackbarConfig && !isUnauthorized) {
        const errorMessage = getMessage?.error
          ? getMessage.error(error)
          : getApiErrorMessage(error, serviceName);

        if (errorMessage) {
          showNotification(errorMessage, { variant: "error" });
        }
      }

      if (!isUnauthorized) {
        console.error("API Error:", error);
      }
      throw error;
    }
  };


export const paginatedApiCall = async <T>(
  path: string,
  pagination: Pagination,
  transformation?: (res: any) => T
): Promise<{ data: T[]; pagination: Pagination }> => {
  const paginationParts = [
    `orderBy=${pagination.sort.sortBy}`,
    `ascending=${pagination.sort.direction === PaginationDirection.Ascending}`,
    `pageSize=${pagination.pageSize}`,
    `pageNumber=${pagination.pageNumber}`,
  ];

  const url = `${api.hostname}${path}${path.includes("?") ? "&" : "?"
    }${paginationParts.join("&")}`;


  const { data } = await axios.get(url);

  return {
    data: transformation ? data.results.map(transformation) : data.results,
    pagination: {
      ...pagination,
      totalPages: data.pagination?.totalPages,
      totalCount: data.pagination?.totalCount,
    },
  };
};
